/**
 * Thin DOMPurify wrapper for sanitising admin-authored HTML before injecting
 * it via `dangerouslySetInnerHTML`.
 *
 * Even though the only writers are admin users, sanitising is cheap defence-in-depth:
 * - protects against compromised admin account injecting <script>
 * - protects against XSS via a future admin-edit endpoint that forgets to escape
 * - keeps inline event handlers (`onclick=...`) stripped
 *
 * We allow common rich-text tags (p, h1-h4, a, img, strong, em, ul/ol/li, blockquote,
 * span/div for class styling, br) and the `style`, `class`, `href`, `src`, `alt`,
 * `target`, `rel` attributes. Everything else (including <script>, <iframe>, event
 * handlers, `javascript:` URLs) is stripped.
 */
import DOMPurify from 'dompurify';

export const sanitizeHtml = (dirty) => {
  if (typeof dirty !== 'string') return '';
  return DOMPurify.sanitize(dirty, {
    USE_PROFILES: { html: true },
    FORBID_TAGS: ['style', 'script', 'iframe', 'object', 'embed', 'form'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur'],
  });
};

/** True if the string contains HTML markup (i.e. came from the rich-text editor). */
export const isHtml = (str) => typeof str === 'string' && /<\/?[a-z][\s\S]*>/i.test(str);

/**
 * Strip all HTML to readable plain text — used for SEO/structured-data and compact
 * card previews where formatting must not appear. Legacy plain text passes through
 * unchanged. Block-level tags become spaces so words don't run together.
 */
export const stripHtml = (html) => {
  if (typeof html !== 'string' || html === '') return '';
  if (!isHtml(html)) return html;
  const spaced = sanitizeHtml(html)
    .replace(/<\/(p|div|h[1-6]|li|tr|blockquote)>/gi, ' ')
    .replace(/<br\s*\/?>/gi, ' ');
  const tmp = document.createElement('div');
  tmp.innerHTML = spaced;
  return (tmp.textContent || tmp.innerText || '').replace(/\s+/g, ' ').trim();
};
